<template>
	<b-form-row>
		<b-col cols="2" v-if="preview">
			<b-avatar rounded size="4rem" variant="light" :src="value && value.url || null" icon="image"></b-avatar>
		</b-col>
		<b-col>
			<b-form-file v-model="file" @input="onFile" :accept="accept" :disabled="uploading > 0" :placeholder="placeholder"></b-form-file>
			<div v-if="progress != null" class="d-flex align-items-center mt-1">
				<b-progress :value="progress" max="100" height="3px" class="flex-fill mr-1"></b-progress>
				<b-button size="sm" variant="link" class="text-danger p-0" @click="cancel()"><b-icon icon="x-circle"></b-icon></b-button>
			</div>
			<div v-else-if="error" class="small text-danger mt-1">⚠ {{ error }}</div>
			<div v-else-if="value && value.url" class="d-flex align-items-center mt-1">
				<span class="small text-muted flex-fill text-truncate">{{ value.filename }}<template v-if="value.size"> ({{ bytesString(value.size) }}B)</template></span>
				<b-button size="sm" variant="link" class="text-danger p-0 ml-1" @click="clear()"><b-icon icon="x-circle"></b-icon></b-button>
			</div>
		</b-col>
	</b-form-row>
</template>

<script>
import util from '@/util.js';

// Single-file uploader (styled like PipiPupuFile) that stores to a MinIO-backed
// API endpoint. `v-model` holds the stored file: { url, filename, size }.
//   - `endpoint`  API route that stores the blob and returns { url }.
//   - `validate`  optional async (file) => errorString|null; a returned string
//                 blocks the upload and is shown as the error.
export default {
	name: 'PipiPupuFileS3',
	props: {
		value: Object,
		accept: String,
		endpoint: { type: String, required: true },
		validate: Function,
		placeholder: { type: String, default: 'Choose or drop a file…' },
		preview: { type: Boolean, default: false },
	},
	data() { return {
		file: null,        // the picker's currently-selected File (transient)
		progress: null,
		error: null,
		uploading: 0,
	}},
	created() { this._abort = null; },
	beforeDestroy() { this._abort?.abort(); },
	methods: {
		async onFile(file) {
			if(!file) return; // programmatic reset
			this.error = null;

			if(this.validate) {
				let err;
				try { err = await this.validate(file); }
				catch(e) { err = e.message || 'Invalid file.'; }
				if(err) { this.error = err; this.file = null; return; }
			}

			const controller = new AbortController();
			this._abort = controller;
			try {
				this.uploading++;
				this.progress = 0;
				const result = await this.$api.uploadToS3(this.endpoint, file, p => { this.progress = p; }, controller.signal);
				this.$emit('input', { url: result.url, filename: file.name, size: file.size });
				this.$emit('uploaded', { url: result.url, filename: file.name, size: file.size });
			} catch(err) {
				if(!controller.signal.aborted) this.error = err.message || 'Upload failed.';
			} finally {
				this.uploading--;
				this.progress = null;
				this._abort = null;
				this.file = null; // reset the picker; `value` (the stored file) is the source of truth
			}
		},
		cancel() {
			this._abort?.abort();
			this.progress = null;
			this.file = null;
		},
		clear() {
			this.$emit('input', null);
			this.error = null;
			this.file = null;
		},
		bytesString(n) { return util.bytesString(n); },
	},
};
</script>
